import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { At, LockKey, ArrowLeft, Sparkle, Microphone, Heart, ChatCircleDots } from "@phosphor-icons/react";import { api, setStoredToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import Logo, { LogoMark } from "@/components/Logo";
import InstallAppBanner from "@/components/InstallAppBanner";

function formatApiErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function describeAuthError(e, mode) {
  const status = e?.response?.status;
  // Status-code specific friendly copy takes priority over generic backend detail.
  if (status === 401) return "Wrong email or password.";
  if (status === 409) return mode === "signup" ? "That email is already registered. Try logging in." : "Account conflict — try again.";
  if (status === 429) return "Too many attempts. Wait a moment and try again.";
  if (status === 400) {
    const d = formatApiErrorDetail(e?.response?.data?.detail);
    return d || "Please check your inputs and try again.";
  }
  if (status >= 500) return "Voxly is having a hiccup. Please try again in a few seconds.";
  // Fall back to backend detail if it provided one
  const detail = formatApiErrorDetail(e?.response?.data?.detail);
  if (detail) return detail;
  // Network / CORS / no response
  if (e?.code === "ERR_NETWORK" || e?.message === "Network Error") {
    return "Can't reach Voxly. Check your internet and try again.";
  }
  if (e?.code === "ECONNABORTED") return "Request timed out. Try again.";
  return "Couldn't sign you in. Please try again.";
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthScreen() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [mode, setMode] = useState("home");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [dob, setDob] = useState("");
  const [confirm18, setConfirm18] = useState(false);
  // "Remember me" — stores the email locally so it's pre-filled next time.
  // (Password itself is never stored by us; the browser's password manager
  // handles that via the autoComplete attributes on the inputs below.)
  const [rememberMe, setRememberMe] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [authError, setAuthError] = useState(null); // { kind: "wrong-pw" | "rate-limit" | "network" | "generic", message }
  const [attempts, setAttempts] = useState(0); // failed login attempts in this session

  // Pre-fill the email field from the last successful "remember me" session.
  useEffect(() => {
    try {
      const saved = localStorage.getItem("voxly:rememberedEmail");
      if (saved) setEmail(saved);
    } catch {
      // localStorage can throw in Safari Private Mode or when storage is
      // disabled. Pre-fill is a nice-to-have; safe to silently skip.
    }
  }, []);

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/feed";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (endpoint) => {
    setSubmitting(true);
    setAuthError(null);
    try {
      const body = endpoint === "/auth/signup"
        ? { email, password, date_of_birth: dob, confirm_18: confirm18 }
        : { email, password, remember: rememberMe };
      const { data } = await api.post(endpoint, body);
      // Persist the JWT in localStorage. The api.js request interceptor will
      // attach it as `Authorization: Bearer ...` on every subsequent call.
      // This is the fix for mobile browsers that silently drop our SameSite=
      // None cookie (Samsung Internet, iOS Safari PWA mode, etc).
      if (data?.access_token) setStoredToken(data.access_token);
      if (data?.user) setUser(data.user);
      // Remember-me handling — store the email so we can pre-fill next time.
      try {
        if (rememberMe && email) localStorage.setItem("voxly:rememberedEmail", email);
        else localStorage.removeItem("voxly:rememberedEmail");
      } catch {
        // Safari Private Mode / storage-disabled environments throw here. We
        // never block the login flow on a remember-me UX preference.
      }
      setAttempts(0);
      if (endpoint === "/auth/signup") {
        navigate("/choose-username", { replace: true });
      } else {
        const u = data.user;
        if (!u?.username) navigate("/choose-username", { replace: true });
        else if (!u.onboarded) navigate("/onboarding", { replace: true });
        else if (!u.verified) navigate("/verify", { replace: true });
        else navigate("/feed", { replace: true });
      }
    } catch (e) {
      const message = describeAuthError(e, mode);
      const status = e?.response?.status;
      let kind = "generic";
      if (status === 401) kind = "wrong-pw";
      else if (status === 429) kind = "rate-limit";
      else if (e?.code === "ERR_NETWORK" || e?.message === "Network Error") kind = "network";
      if (mode === "login") {
        setAuthError({ kind, message });
        setAttempts((n) => n + 1);
      } else {
        toast.error(message); // signup keeps the toast UX
      }
    } finally { setSubmitting(false); }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-background text-foreground">
      {/* Vibrant gradient backdrop with floating "previews" — gives a real social-media energy */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute -top-32 -left-24 h-[28rem] w-[28rem] rounded-full bg-primary/40 blur-3xl" />
        <div className="absolute top-1/3 -right-24 h-[24rem] w-[24rem] rounded-full bg-fuchsia-500/25 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-[20rem] w-[20rem] rounded-full bg-cyan-500/20 blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto max-w-md min-h-screen flex flex-col sm:border-x sm:border-white/5">
        <header className="flex items-center justify-between pt-8 px-6">
          <Logo size={40} />
          {mode !== "home" && (
            <button onClick={() => setMode("home")} className="text-muted-foreground hover:text-foreground p-2 rounded-full" data-testid="auth-back">
              <ArrowLeft size={20} />
            </button>
          )}
        </header>

        {mode === "home" && (
          <div className="px-6 pt-12 flex-1">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-3 py-1 text-xs font-mono uppercase tracking-widest text-muted-foreground">
              <Sparkle size={12} weight="fill" className="text-primary" /> Voice-first social
            </div>
            <h1 className="mt-5 font-heading text-5xl sm:text-6xl font-black leading-[0.95] tracking-tight">
              Talk to the<br/>world out loud.
            </h1>
            <p className="mt-4 text-base text-muted-foreground max-w-md">
              Voxly is a social network for human voices. Post photos & videos, drop voice comments, share 24-hour voice stories.
            </p>

            {/* Floating sample card to show what the app is */}
            <SamplePostPreview />
          </div>
        )}

        <div className="mt-auto px-6 pb-10 pt-6">
          <div className="rounded-[28px] glass border border-white/10 p-6 sm:p-8">
            {mode === "home" && (
              <>
                <button
                  data-testid="google-login-button"
                  onClick={handleGoogleLogin}
                  className="w-full rounded-full bg-white text-black py-4 text-base font-semibold flex items-center justify-center gap-3 active:scale-[0.98] transition glow-hover"
                >
                  <GoogleIcon /> Continue with Google
                </button>

                <div className="my-4 flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-muted-foreground font-mono">
                  <span className="h-px flex-1 bg-white/10" /> or <span className="h-px flex-1 bg-white/10" />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => setMode("login")} data-testid="open-login"
                    className="rounded-full border border-white/15 py-4 text-base font-semibold hover:border-white/30 active:scale-[0.98] transition">
                    Log in
                  </button>
                  <button onClick={() => setMode("signup")} data-testid="open-signup"
                    className="rounded-full bg-primary text-primary-foreground py-4 text-base font-semibold glow-hover active:scale-[0.98] transition">
                    Sign up
                  </button>
                </div>

                <button
                  type="button"
                  data-testid="home-magic-link"
                  onClick={() => navigate("/magic-link")}
                  className="mt-3 w-full rounded-full bg-secondary/60 border border-white/10 py-3.5 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-secondary transition"
                >
                  <Sparkle size={14} weight="fill" className="text-primary" /> Sign in with magic link
                </button>

                <p className="mt-4 text-[11px] text-muted-foreground/70 text-center">
                  Read our <Link to="/legal/privacy" className="underline">Privacy Policy</Link> and <Link to="/legal/terms" className="underline">Terms of Service</Link>.
                </p>
              </>
            )}

            {(mode === "login" || mode === "signup") && (
              <form onSubmit={(e) => { e.preventDefault(); submit(mode === "signup" ? "/auth/signup" : "/auth/login"); }} className="space-y-3">
                <h2 className="font-heading text-3xl font-black tracking-tight">
                  {mode === "signup" ? "Create your voice." : "Welcome back."}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {mode === "signup" ? "Join the conversation in seconds." : "Sign in to keep talking."}
                </p>

                {mode === "signup" && (
                  <p className="text-xs text-muted-foreground -mt-1">
                    Next step: pick a unique <span className="text-primary font-semibold">@username</span> + your name.
                  </p>
                )}
                <Field icon={At} testid={`${mode}-email`} placeholder="Email" type="email" value={email} onChange={setEmail} autoComplete="email" />
                <Field icon={LockKey} testid={`${mode}-password`} placeholder="Password" type="password" value={password} onChange={setPassword} autoComplete={mode === "signup" ? "new-password" : "current-password"} />

                <div className="flex items-center justify-between -mt-1">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      data-testid={`${mode}-remember-me`}
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="h-4 w-4 accent-primary"
                    />
                    <span className="text-xs text-muted-foreground">Remember me</span>
                  </label>
                  {mode === "login" && (
                    <button
                      type="button"
                      data-testid="forgot-password-link"
                      onClick={() => navigate("/forgot-password" + (email ? `?email=${encodeURIComponent(email)}` : ""))}
                      className="text-xs font-semibold text-primary hover:text-primary/80 active:scale-95 transition"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>

                {mode === "login" && authError && (
                  <SignInRecoveryPanel
                    error={authError}
                    attempts={attempts}
                    email={email}
                    onDismiss={() => setAuthError(null)}
                    navigate={navigate}
                  />
                )}

                {mode === "signup" && (
                  <>
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Date of birth (you must be 18+)</label>
                      <input
                        data-testid="signup-dob"
                        type="date"
                        value={dob}
                        onChange={(e) => setDob(e.target.value)}
                        max={new Date(Date.now() - 18*365.25*24*3600*1000).toISOString().slice(0, 10)}
                        required
                        className="w-full bg-secondary/70 border border-border rounded-2xl px-4 py-3 text-sm outline-none focus:border-primary/60"
                      />
                    </div>
                    <label className="flex items-start gap-3 cursor-pointer pt-1">
                      <input type="checkbox" data-testid="signup-confirm-18" checked={confirm18} onChange={(e) => setConfirm18(e.target.checked)} className="mt-1 h-4 w-4 accent-primary" />
                      <span className="text-xs text-muted-foreground">
                        I confirm I am at least 18 years old and that this is my real identity.
                      </span>
                    </label>
                  </>
                )}

                <button
                  type="submit" disabled={submitting} data-testid={`${mode}-submit`}
                  className="w-full rounded-full bg-primary text-primary-foreground py-4 text-base font-semibold glow-hover disabled:opacity-50"
                >
                  {submitting ? "Please wait…" : (mode === "signup" ? "Create account" : "Log in")}
                </button>

                {mode === "login" && (
                  <div className="relative pt-2">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-px bg-border/60" />
                      <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground/70">or</span>
                      <div className="flex-1 h-px bg-border/60" />
                    </div>
                    <button
                      type="button"
                      data-testid="magic-link-link"
                      onClick={() => navigate("/magic-link")}
                      className="mt-3 w-full rounded-full bg-secondary/60 hover:bg-secondary border border-white/10 text-foreground py-3.5 text-sm font-semibold flex items-center justify-center gap-2 transition"
                    >
                      <Sparkle size={16} weight="fill" className="text-primary" />
                      Sign in with magic link
                    </button>
                  </div>
                )}

                <p className="text-xs text-center text-muted-foreground pt-1">
                  {mode === "signup" ? "Already have an account? " : "New to Voxly? "}
                  <button type="button" onClick={() => setMode(mode === "signup" ? "login" : "signup")} className="text-primary font-semibold" data-testid="auth-mode-toggle">
                    {mode === "signup" ? "Log in" : "Sign up"}
                  </button>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SamplePostPreview() {
  return (
    <div className="mt-8 relative h-[210px] sm:h-[230px]" data-testid="auth-sample">
      {/* Big post card */}
      <div className="absolute left-0 right-12 top-2 rounded-3xl overflow-hidden bg-card border border-white/10 shadow-2xl rotate-[-3deg]">
        <img src="https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?w=800&q=70" alt="" className="w-full h-32 object-cover" />
        <div className="px-3 py-2 flex items-center gap-2 bg-secondary/70">
          <div className="h-8 w-8 rounded-full bg-primary/30 flex items-center justify-center">
            <Microphone size={14} weight="fill" className="text-primary" />
          </div>
          <div className="flex-1 flex items-center gap-[2px] h-5">
            {[40, 80, 60, 90, 30, 70, 50, 65, 45, 85, 55, 75, 35, 60, 80, 50, 40, 70].map((h, i) => (
              <div key={`bar-${i}-${h}`} className={`flex-1 rounded-full ${i < 8 ? "bg-primary" : "bg-white/20"}`} style={{ height: `${h}%` }} />
            ))}
          </div>
          <span className="text-[10px] font-mono text-muted-foreground">0:14</span>
        </div>
        <div className="px-3 py-2 flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1 text-primary"><Heart size={12} weight="fill" /> 248</span>
          <span className="flex items-center gap-1 text-muted-foreground"><ChatCircleDots size={12} /> 32</span>
        </div>
      </div>
      {/* Smaller floating tile */}
      <div className="absolute right-0 top-12 w-32 rounded-2xl overflow-hidden bg-card border border-white/10 shadow-xl rotate-[6deg]">
        <img src="https://images.unsplash.com/photo-1511632765486-a01980e01a18?w=400&q=70" alt="" className="w-full h-20 object-cover" />
        <div className="px-2 py-2 flex items-center gap-1">
          <LogoMark size={14} />
          <span className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">voice reply</span>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, placeholder, value, onChange, type = "text", testid, autoComplete }) {
  const fallbackAutoComplete = type === "password" ? "current-password" : (type === "email" ? "email" : undefined);
  return (
    <div className="flex items-center gap-3 bg-secondary/70 border border-white/10 rounded-2xl px-4 py-4 focus-within:border-primary/60 transition">
      <Icon size={20} className="text-muted-foreground" />
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        autoComplete={autoComplete || fallbackAutoComplete}
        className="flex-1 bg-transparent outline-none text-base placeholder:text-muted-foreground" required />
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.19V7.07H2.18a11 11 0 0 0 0 9.87l3.66-2.85z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
    </svg>
  );
}

/** Friendly recovery panel that appears right under the password field after a failed login.
 *  Surfaces "Reset password" and "Magic link" as primary actions — no hunting through small links. */
function SignInRecoveryPanel({ error, attempts, email, onDismiss, navigate }) {
  const goReset = () => {
    if (email) sessionStorage.setItem("voxly:lastEmail", email);
    navigate("/forgot-password" + (email ? `?email=${encodeURIComponent(email)}` : ""));
  };
  const goMagic = () => {
    if (email) sessionStorage.setItem("voxly:lastEmail", email);
    navigate("/magic-link" + (email ? `?email=${encodeURIComponent(email)}` : ""));
  };

  // Headline / body per error kind
  const headline = {
    "wrong-pw": attempts >= 2 ? "Still not working?" : "Can't sign in?",
    "rate-limit": "Slow down a sec",
    "network": "Can't reach Voxly",
    "generic": "Sign-in failed",
  }[error.kind] || "Sign-in failed";

  return (
    <div
      data-testid="signin-recovery-panel"
      className="rounded-2xl border border-destructive/30 bg-destructive/10 p-4 space-y-3"
      role="alert"
    >
      <div className="flex items-start gap-3">
        <div className="h-8 w-8 shrink-0 rounded-full bg-destructive/20 flex items-center justify-center text-destructive font-bold">!</div>
        <div className="flex-1 min-w-0">
          <p className="font-heading text-base font-bold text-foreground">{headline}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{error.message}</p>
        </div>
        <button onClick={onDismiss} aria-label="Dismiss" data-testid="recovery-dismiss" className="text-muted-foreground hover:text-foreground p-1">
          <ArrowLeft size={14} className="rotate-45" />
        </button>
      </div>

      {error.kind !== "rate-limit" && error.kind !== "network" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
          <button
            type="button"
            onClick={goMagic}
            data-testid="recovery-magic-link"
            className="w-full rounded-xl bg-primary text-primary-foreground py-3 px-3 text-sm font-semibold flex items-center justify-center gap-2 glow-hover"
          >
            <Sparkle size={15} weight="fill" /> Sign in with magic link
          </button>
          <button
            type="button"
            onClick={goReset}
            data-testid="recovery-reset-password"
            className="w-full rounded-xl bg-card border border-white/15 text-foreground py-3 px-3 text-sm font-semibold flex items-center justify-center gap-2 hover:border-primary/60 transition"
          >
            <LockKey size={15} /> Reset password
          </button>
        </div>
      )}

      <div className="pt-1 border-t border-destructive/15 -mx-1 px-1">
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          <span className="font-mono uppercase tracking-widest text-[10px] text-muted-foreground/70">Tip</span> · You only need your <span className="text-foreground font-semibold">email</span> to sign in — your <span className="text-foreground font-semibold">@username</span> is shown the moment you're back in.{" "}
          <span className="opacity-80">Forgot your email too? Mail </span><a href="mailto:rekan@voxly.app" className="text-primary font-semibold">rekan@voxly.app</a>
          <span className="opacity-80"> with any old usernames + signup details and we'll find your account.</span>
        </p>
      </div>
      <InstallAppBanner />
    </div>
  );
}
